import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, Info, Check, Loader2 } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { CommsToggleSwitch, DeliverySegmented } from "./shared";

const REMINDER_ORDER = ["r48h", "r24h", "r2h"];
const REMINDER_LABEL = {
    r48h: "48 hours before",
    r24h: "24 hours before",
    r2h: "2 hours before",
};

// Sprint 28 — portal-owned client communications settings.
// These are separate from the advanced "Rules" — they control the four
// operational triggers Angela cares about: confirmation, reminders,
// cancellation, reschedule. Saves live to /api/staff/automation-settings.
export function PortalCommsSettings({
    rules = [],
    emailAvailable = true,
    waAvailable = false,
    waAutomationStatuses = {},
    onRulesChanged,
}) {
    const [settings, setSettings] = useState(null);
    const [error, setError] = useState(null);
    const [savedAt, setSavedAt] = useState(null); // for "Saved" indicator (Sprint 30)
    const [saving, setSaving] = useState(false);

    const load = () => {
        setError(null);
        staffApi.getAutomationSettings().then(setSettings).catch(setError);
    };
    useEffect(load, []);

    const save = async (nextPatch) => {
        // Merge patch into full settings so PUT contract stays whole-object.
        const merged = { ...(settings || {}), ...nextPatch };
        setSaving(true);
        try {
            const saved = await staffApi.updateAutomationSettings(merged);
            setSettings(saved);
            setSavedAt(Date.now());
            toast.success("Saved");
        } catch (e) {
            toast.error(e?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const bookingRule = useMemo(
        () => rules.find((r) => r.trigger === "booking_created" && r.active)
            || rules.find((r) => r.trigger === "booking_created")
            || null,
        [rules]
    );

    const reminderRule = useMemo(
        () => rules.find((r) => r.trigger === "reminder_before_appointment" && r.active)
            || rules.find((r) => r.trigger === "reminder_before_appointment")
            || null,
        [rules]
    );

    const cancellationRule = useMemo(
        () => rules.find((r) => r.trigger === "appointment_cancelled" && r.active)
            || rules.find((r) => r.trigger === "appointment_cancelled")
            || null,
        [rules]
    );

    const rescheduleRule = useMemo(
        () => rules.find((r) => r.trigger === "appointment_rescheduled" && r.active)
            || rules.find((r) => r.trigger === "appointment_rescheduled")
            || null,
        [rules]
    );

    const deliveryForRule = (rule) => {
        const channels = (rule?.channels || ["email"]).map((c) => (c || "").toLowerCase());
        const hasEmail = channels.includes("email");
        const hasWa = channels.includes("whatsapp");
        if (hasEmail && hasWa) return "both";
        if (hasWa) return "whatsapp";
        return "email";
    };

    const saveRuleDelivery = async (rule, choice, label) => {
        if (!rule || saving) return;

        const channels =
            choice === "both" ? ["email", "whatsapp"]
            : choice === "whatsapp" ? ["whatsapp"]
            : ["email"];

        setSaving(true);
        try {
            await staffApi.commsPatchRule(rule.id, { channels });
            setSavedAt(Date.now());
            toast.success(`${label} delivery updated`);
            if (onRulesChanged) await onRulesChanged();
        } catch (e) {
            toast.error(e?.message || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    const bookingDelivery = deliveryForRule(bookingRule);
    const reminderDelivery = deliveryForRule(reminderRule);
    const cancellationDelivery = deliveryForRule(cancellationRule);
    const rescheduleDelivery = deliveryForRule(rescheduleRule);

    const bookingWaState =
        waAutomationStatuses.booking_confirmation || { state: "unavailable" };

    const cancellationWaState =
        waAutomationStatuses.cancellation_notice || { state: "unavailable" };

    const rescheduleWaState =
        waAutomationStatuses.reschedule_notice || { state: "unavailable" };

    if (error) return <ErrorState error={error} onRetry={load} />;
    if (!settings) return <Skeleton className="h-48" />;

    const globalReminders = (settings.reminders && settings.reminders.global) || {};

    const saveGlobalReminder = (key, patch) => {
        const nextGlobal = {
            ...(settings.reminders?.global || {}),
            [key]: { ...(settings.reminders?.global?.[key] || {}), ...patch },
        };
        save({ reminders: { ...(settings.reminders || {}), global: nextGlobal } });
    };

    return (
        <div className="space-y-4" data-testid="portal-comms-settings">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h2 className="font-serif text-2xl text-white">Client messaging</h2>
                    <p className="text-sm text-white/50 mt-1 max-w-2xl">
                        The essential messages the portal sends to your clients. Delivery can use
                        Email, WhatsApp or both where available.
                    </p>
                </div>
                <SavedBadge saving={saving} savedAt={savedAt} />
            </div>

            <div className="rounded-2xl border hairline bg-white/[0.02] p-4" data-testid="cfg-booking-confirmation">
                <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-gold/70 shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium">Booking confirmation</h3>
                        <p className="text-[12px] text-white/55 mt-1">
                            Sent immediately when a booking is created (public or manual).
                        </p>
                    </div>
                    <ToggleSwitch
                        on={!!settings.booking_confirmation_enabled}
                        busy={saving}
                        onToggle={(v) => save({ booking_confirmation_enabled: v })}
                        testId="cfg-booking-confirmation-toggle"
                    />
                </div>

                <div className="mt-4 pt-3 border-t hairline">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-white/45 mb-2">
                        Delivery via
                    </div>

                    {bookingRule ? (
                        <DeliverySegmented
                            value={bookingDelivery}
                            onChange={(choice) => saveRuleDelivery(bookingRule, choice, "Booking confirmation")}
                            busy={saving}
                            testIdPrefix="cfg-booking-confirmation-delivery"
                            emailAvailable={emailAvailable}
                            waAvailable={waAvailable}
                            waReady={bookingWaState.state === "ready"}
                            waPreparing={bookingWaState.state === "preparing"}
                            waAttention={bookingWaState.state === "needs_attention"}
                        />
                    ) : (
                        <p className="text-[11px] text-amber-200/80">
                            Booking confirmation delivery rule is not configured.
                        </p>
                    )}
                </div>
            </div>

            <div className="rounded-2xl border hairline bg-white/[0.02] p-4" data-testid="cfg-reminders">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                        <h3 className="text-white font-medium">Appointment reminders</h3>
                        <p className="text-[12px] text-white/55 mt-1">
                            Pick which reminders should fire before every appointment. Reminders for
                            appointments that are already too close are automatically skipped.
                        </p>
                    </div>
                </div>
                <div className="mt-3 space-y-2">
                    {REMINDER_ORDER.map((key) => {
                        const cfg = globalReminders[key] || { enabled: false, offset_minutes: 0 };
                        return (
                            <div key={key} className="flex items-center justify-between rounded-xl border hairline bg-black/20 px-3 py-2" data-testid={`cfg-reminder-${key}`}>
                                <div className="min-w-0">
                                    <div className="text-white text-sm font-medium">{REMINDER_LABEL[key]}</div>
                                    <div className="text-[11px] text-white/45">
                                        {key === "r24h" ? "Default on" : "Off unless enabled"}
                                    </div>
                                </div>
                                <ToggleSwitch
                                    on={!!cfg.enabled}
                                    busy={saving}
                                    onToggle={(v) => saveGlobalReminder(key, { enabled: v })}
                                    testId={`cfg-reminder-${key}-toggle`}
                                />
                            </div>
                        );
                    })}
                </div>
                <div className="mt-3 flex items-center gap-2 text-[11px] text-white/40">
                    <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Category-specific overrides can be configured per treatment when needed.
                </div>

                <div className="mt-4 pt-3 border-t hairline">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-white/45 mb-2">
                        Delivery via
                    </div>

                    {reminderRule ? (
                        <DeliverySegmented
                            value={reminderDelivery}
                            onChange={(choice) => saveRuleDelivery(reminderRule, choice, "Appointment reminders")}
                            busy={saving}
                            testIdPrefix="cfg-reminders-delivery"
                            emailAvailable={emailAvailable}
                            waAvailable={waAvailable}
                            waReady={["reminder_48h", "reminder_24h", "reminder_2h"].every(
                                (id) => (waAutomationStatuses[id] || {}).state === "ready"
                            )}
                            waPreparing={["reminder_48h", "reminder_24h", "reminder_2h"].some(
                                (id) => (waAutomationStatuses[id] || {}).state === "preparing"
                            )}
                            waAttention={["reminder_48h", "reminder_24h", "reminder_2h"].some(
                                (id) => (waAutomationStatuses[id] || {}).state === "needs_attention"
                            )}
                        />
                    ) : (
                        <p className="text-[11px] text-amber-200/80">
                            Appointment reminder delivery rule is not configured.
                        </p>
                    )}
                </div>
            </div>

            <OperationalDeliveryPanel
                testId="cfg-cancellation"
                title="Cancellation notification"
                description="Sent when you mark an appointment as cancelled. Pending reminders are automatically stopped."
                checked={settings.cancellation_enabled}
                onChange={(v) => save({ cancellation_enabled: v })}
                busy={saving}
                rule={cancellationRule}
                delivery={cancellationDelivery}
                onDeliveryChange={(choice) => saveRuleDelivery(cancellationRule, choice, "Cancellation")}
                emailAvailable={emailAvailable}
                waAvailable={waAvailable}
                waState={cancellationWaState}
            />

            <OperationalDeliveryPanel
                testId="cfg-reschedule"
                title="Reschedule notification"
                description="Sent when you reschedule an appointment. Reminders are refreshed for the new time."
                checked={settings.reschedule_enabled}
                onChange={(v) => save({ reschedule_enabled: v })}
                busy={saving}
                rule={rescheduleRule}
                delivery={rescheduleDelivery}
                onDeliveryChange={(choice) => saveRuleDelivery(rescheduleRule, choice, "Reschedule")}
                emailAvailable={emailAvailable}
                waAvailable={waAvailable}
                waState={rescheduleWaState}
            />
        </div>
    );
}

function OperationalDeliveryPanel({
    title,
    description,
    checked,
    onChange,
    testId,
    busy,
    rule,
    delivery,
    onDeliveryChange,
    emailAvailable,
    waAvailable,
    waState = { state: "unavailable" },
}) {
    return (
        <div className="rounded-2xl border hairline bg-white/[0.02] p-4" data-testid={testId}>
            <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-gold/70 shrink-0 mt-0.5" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium">{title}</h3>
                    <p className="text-[12px] text-white/55 mt-1">{description}</p>
                </div>
                <ToggleSwitch
                    on={!!checked}
                    busy={busy}
                    onToggle={onChange}
                    testId={`${testId}-toggle`}
                />
            </div>

            <div className="mt-4 pt-3 border-t hairline">
                <div className="text-[11px] uppercase tracking-[0.14em] text-white/45 mb-2">
                    Delivery via
                </div>

                {rule ? (
                    <DeliverySegmented
                        value={delivery}
                        onChange={onDeliveryChange}
                        busy={busy}
                        testIdPrefix={`${testId}-delivery`}
                        emailAvailable={emailAvailable}
                        waAvailable={waAvailable}
                        waReady={waState.state === "ready"}
                        waPreparing={waState.state === "preparing"}
                        waAttention={waState.state === "needs_attention"}
                    />
                ) : (
                    <p className="text-[11px] text-amber-200/80">
                        Delivery rule is not configured.
                    </p>
                )}
            </div>
        </div>
    );
}

function TogglePanel({ title, description, checked, onChange, testId, busy }) {
    return (
        <div className="rounded-2xl border hairline bg-white/[0.02] p-4 flex items-start gap-3" data-testid={testId}>
            <Mail className="h-5 w-5 text-gold/70 shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium">{title}</h3>
                <p className="text-[12px] text-white/55 mt-1">{description}</p>
            </div>
            <ToggleSwitch on={!!checked} busy={busy} onToggle={onChange} testId={`${testId}-toggle`} />
        </div>
    );
}

// Sprint 36.4: consolidated to the shared `CommsToggleSwitch` so every
// switch on the Communications page uses identical geometry + colour states.
function ToggleSwitch(props) {
    return <CommsToggleSwitch {...props} />;
}

// Sprint 30: clear saved/saving feedback so Angela knows the change stuck.
function SavedBadge({ saving, savedAt }) {
    const recent = savedAt && Date.now() - savedAt < 4000;
    if (saving) {
        return (
            <div className="text-[11px] text-white/60 inline-flex items-center gap-1.5" data-testid="portal-comms-saving">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
            </div>
        );
    }
    if (recent) {
        return (
            <div className="text-[11px] text-emerald-300 inline-flex items-center gap-1.5" data-testid="portal-comms-saved">
                <Check className="h-3.5 w-3.5" /> Saved
            </div>
        );
    }
    return null;
}
